/**
 * Voice endpoint-quality metrics (framework-free, testable).
 *
 * The one number that tells us whether the native cut-off bug is actually fixed
 * is the FALSE-ENDPOINT rate: how often we auto-submit a turn while the user
 * wasn't really done. We can't read the user's mind, so we track measurable
 * proxies per turn and let a device session surface them:
 *
 *  - which path ended the turn (genuine acoustic pause vs the maxSilenceMs cap
 *    vs the legacy result-gap fallback) — cap/fallback ends are the suspect ones;
 *  - trailing silence we actually waited before ending (short ⇒ premature);
 *  - utterance duration (very short ⇒ likely clipped);
 *  - first-word-after-open latency (consistently tiny ⇒ users were queued up to
 *    keep talking, i.e. we'd been cutting them off).
 *
 * Everything is in-memory and best-effort; a metric with zero samples reports as
 * null ("unverified"), never as a pass. This is instrumentation, not analytics —
 * nothing leaves the device.
 */

export type EndpointReason =
  /** Genuine end: acoustically quiet for quietMs AND transcript stable. */
  | "acoustic"
  /** Safety cap: transcript went idle while the mic still read as "voiced". */
  | "maxcap"
  /** No volume events on this device — the legacy result-gap timer ended it. */
  | "fallback";

const REASONS: EndpointReason[] = ["acoustic", "maxcap", "fallback"];

export interface EndpointStat {
  samples: number;
  medianMs: number | null;
  p95Ms: number | null;
  minMs: number | null;
  maxMs: number | null;
}

export interface EndpointSnapshot {
  submitted: number;
  byReason: Record<EndpointReason, number>;
  /**
   * Conservative premature-risk rate: share of auto-submits that ended via the
   * cap or the fallback timer rather than a genuine acoustic pause. Not a proof
   * of a false endpoint, but the honest upper-bound proxy until barge-in lets us
   * detect "user kept talking" directly. Null when nothing was submitted.
   */
  prematureRiskRate: number | null;
  trailingSilenceMs: EndpointStat;
  utteranceMs: EndpointStat;
  firstWordAfterOpenMs: EndpointStat;
}

function percentile(sortedAsc: number[], p: number): number | null {
  if (sortedAsc.length === 0) return null;
  if (sortedAsc.length === 1) return sortedAsc[0];
  const idx = Math.min(
    sortedAsc.length - 1,
    Math.max(0, Math.ceil((p / 100) * sortedAsc.length) - 1),
  );
  return sortedAsc[idx];
}

function stat(raw: number[]): EndpointStat {
  const sorted = [...raw].sort((a, b) => a - b);
  return {
    samples: sorted.length,
    medianMs: percentile(sorted, 50),
    p95Ms: percentile(sorted, 95),
    minMs: sorted.length ? sorted[0] : null,
    maxMs: sorted.length ? sorted[sorted.length - 1] : null,
  };
}

export class EndpointMetrics {
  private submitted = 0;
  private readonly reason = new Map<EndpointReason, number>();
  private readonly trailingSilence: number[] = [];
  private readonly utterance: number[] = [];
  private readonly firstWord: number[] = [];

  /** Record one auto-submitted turn and how/when it ended. */
  recordTurn(input: {
    reason: EndpointReason;
    trailingSilenceMs: number;
    utteranceMs: number;
  }): void {
    this.submitted += 1;
    this.reason.set(input.reason, (this.reason.get(input.reason) ?? 0) + 1);
    if (Number.isFinite(input.trailingSilenceMs) && input.trailingSilenceMs >= 0) {
      this.trailingSilence.push(input.trailingSilenceMs);
    }
    if (Number.isFinite(input.utteranceMs) && input.utteranceMs >= 0) {
      this.utterance.push(input.utteranceMs);
    }
  }

  /** Time from the mic actually capturing to the user's first word this turn. */
  recordFirstWord(ms: number): void {
    if (Number.isFinite(ms) && ms >= 0) this.firstWord.push(ms);
  }

  snapshot(): EndpointSnapshot {
    const byReason = {} as Record<EndpointReason, number>;
    let suspect = 0;
    for (const r of REASONS) {
      const n = this.reason.get(r) ?? 0;
      byReason[r] = n;
      if (r !== "acoustic") suspect += n;
    }
    return {
      submitted: this.submitted,
      byReason,
      prematureRiskRate: this.submitted === 0 ? null : suspect / this.submitted,
      trailingSilenceMs: stat(this.trailingSilence),
      utteranceMs: stat(this.utterance),
      firstWordAfterOpenMs: stat(this.firstWord),
    };
  }

  reset(): void {
    this.submitted = 0;
    this.reason.clear();
    this.trailingSilence.length = 0;
    this.utterance.length = 0;
    this.firstWord.length = 0;
  }
}

/** Process-wide singleton — the voice loop records into this. */
export const endpointMetrics = new EndpointMetrics();

/** Dev-only: print the running snapshot so a dev-build device session shows it. */
export function logEndpointSnapshot(): void {
  if (typeof __DEV__ === "undefined" || !__DEV__) return;
  const s = endpointMetrics.snapshot();
  const pct =
    s.prematureRiskRate === null
      ? "n/a"
      : `${Math.round(s.prematureRiskRate * 100)}%`;
  console.log(
    `[voice/endpoint] submitted=${s.submitted} premature-risk=${pct} ` +
      `byReason=${JSON.stringify(s.byReason)} ` +
      `trailingSilenceMs(med/p95)=${s.trailingSilenceMs.medianMs}/${s.trailingSilenceMs.p95Ms} ` +
      `utteranceMs(med)=${s.utteranceMs.medianMs} ` +
      `firstWordAfterOpenMs(med)=${s.firstWordAfterOpenMs.medianMs}`,
  );
}
